'use client';

import { useEffect, useMemo, useState, type FC } from 'react';
import { cn } from '../../../utils/cn';
import { FaChevronDown } from '../../../components/icons';
import { wikiProse } from '../wikiTypography';

interface WikiTocProps {
  title: string;
  entries: { id: string; text: string }[];
}

const scrollParentOf = (element: HTMLElement): HTMLElement | null => {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
};

const useActiveHeading = (ids: string[]): string | null => {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    const headings = ids.flatMap((id) => {
      const heading = document.getElementById(id);
      return heading ? [heading] : [];
    });
    const scroller = headings[0] ? scrollParentOf(headings[0]) : null;
    const pick = () => {
      const top = scroller ? scroller.getBoundingClientRect().top : 0;
      const height = scroller ? scroller.clientHeight : window.innerHeight;
      const atBottom = scroller
        ? scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4
        : window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 4;
      // WHY: последний раздел (FAQ) часто не доходит до линии — у конца страницы активен он
      if (atBottom) {
        setActive(headings.at(-1)?.id ?? null);
        return;
      }
      const line = top + height * 0.3;
      const passed = headings.filter((heading) => heading.getBoundingClientRect().top <= line);
      setActive((passed.at(-1) ?? headings[0])?.id ?? null);
    };
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(pick);
    };
    pick();
    // WHY: страница прокручивается внутри контейнера раскладки, а не window; scroll не всплывает, ловим его на фазе захвата
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
    };
  }, [ids]);
  return active;
};

export const WikiTocMobile: FC<WikiTocProps> = ({ title, entries }) => (
  <nav aria-label={title} className="lg:hidden">
    <details className="group rounded-[18px] bg-bg-secondary [&_summary::-webkit-details-marker]:hidden">
      <summary className="flex min-h-12 cursor-pointer list-none items-center gap-2 px-4 text-[15px] font-semibold text-label-primary outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-blue">
        {title}
        <span className="text-[13px] font-medium text-label-secondary">{entries.length}</span>
        <FaChevronDown aria-hidden size={12} className="ms-auto text-label-secondary transition-transform group-open:rotate-180" />
      </summary>
      <ol className="m-0 flex list-decimal flex-col gap-0.5 pb-3 pe-4 ps-9 text-[15px] leading-[21px] marker:text-label-secondary">
        {entries.map((entry) => (
          <li key={entry.id}>
            <a href={`#${entry.id}`} className="inline-flex min-h-10 items-center text-accent-blue hover:underline">
              {wikiProse(entry.text)}
            </a>
          </li>
        ))}
      </ol>
    </details>
  </nav>
);

export const WikiTocDesktop: FC<WikiTocProps & { className?: string }> = ({ title, entries, className }) => {
  const ids = useMemo(() => entries.map((e) => e.id), [entries]);
  const active = useActiveHeading(ids);
  return (
    <nav aria-labelledby="wiki-toc" className={cn('hidden lg:block', className)}>
      <h2 id="wiki-toc" className="m-0 mb-3 text-[13px] font-semibold uppercase tracking-[0.5px] text-label-secondary">
        {title}
      </h2>
      <ol className="m-0 flex list-none flex-col gap-0.5 border-s border-white/[0.1] p-0">
        {entries.map((entry) => (
          <li key={entry.id}>
            <a
              href={`#${entry.id}`}
              aria-current={entry.id === active ? 'location' : undefined}
              className={cn(
                '-ms-px block border-s py-2.5 ps-4 text-[14px] leading-[20px] transition-colors hover:border-label-primary hover:text-label-primary',
                entry.id === active ? 'border-label-primary font-medium text-label-primary' : 'border-transparent text-label-secondary',
              )}
            >
              {wikiProse(entry.text)}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};
